import { useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import type { StoryRevision } from '../../api/types';

interface Props {
  current: StoryRevision;
  previous: StoryRevision | null;
  defaultExpanded?: boolean;
}

/**
 * Side-by-side text diff between a story revision and the previous one.
 * Collapsed by default — click the header to expand. The newest two
 * revisions on a story page should pass `defaultExpanded`.
 */
export default function RevisionDiff({ current, previous, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 text-left"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-gray-800">revision {current.revision}</span>
          <SourceBadge source={current.source} />
          <span className="text-xs text-gray-500">
            {new Date(current.createdAt).toLocaleString()}
          </span>
          {current.refinementSessionId && (
            <code className="text-xs text-gray-500">
              session {current.refinementSessionId.slice(0, 8)}…
            </code>
          )}
        </div>
        <span className="text-gray-400">{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="p-3 bg-white">
          {previous ? (
            <ReactDiffViewer
              oldValue={previous.rawText}
              newValue={current.rawText}
              splitView
              compareMethod={DiffMethod.WORDS}
              hideLineNumbers={false}
              styles={{
                contentText: { fontSize: '12px', fontFamily: 'ui-monospace, monospace' },
              }}
            />
          ) : (
            <div>
              <div className="text-xs text-gray-500 mb-2">First revision — no diff baseline.</div>
              <pre className="text-xs whitespace-pre-wrap font-mono bg-gray-50 border border-gray-200 rounded p-3">
                {current.rawText}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SourceBadge({ source }: { source: StoryRevision['source'] }) {
  const cls = {
    submit: 'bg-blue-100 text-blue-800',
    refinement_resubmit: 'bg-amber-100 text-amber-800',
    admin_edit: 'bg-purple-100 text-purple-800',
  }[source];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {source}
    </span>
  );
}
